import { ProCard } from '@ant-design/pro-components'
import { sync } from '@app/icare/client/sync'
import {
  type Clinic,
  type ClinicIdType,
  ClinicSchema,
  MedicalSpecialityEnum,
} from '@app/icare/entities/clinic/schema/Clinic'
import { EntityDrawerShell } from '@app/icare/form/EntityDrawerShell'
import { AddressFieldGroup } from '@app/icare/form/group/AddressFieldGroup'
import { useEntityDrawer } from '@app/icare/hooks/useEntityDrawer'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import type { SectionDef } from '@pkg/form/useSectionFlow'
import { zodDefaultInstance } from '@pkg/shared/form'
import { createTypedModal, type EditModalProps, type EditModalResult } from '@pkg/ui/modal'
import { Flex } from 'antd'

const sections = [
  { name: 'basic', title: t`Allgemein` },
  { name: 'misc', title: t`Sonstiges` },
] as const satisfies readonly SectionDef[]

type ClinicSection = (typeof sections)[number]['name']

type ClinicEditDrawerProps =
  | (EditModalProps<Clinic, ClinicSection, ClinicIdType> & {
      showGlobalImpactWarning?: boolean
    })
  | undefined

type ClinicEditDrawerShow = (props?: ClinicEditDrawerProps) => Promise<EditModalResult<Clinic>>

export const ClinicEditDrawer = createTypedModal<ClinicEditDrawerProps, EditModalResult<Clinic>, ClinicEditDrawerShow>(
  props => {
    const drawer = useEntityDrawer({
      schema: ClinicSchema,
      entityId: props?.id,
      sections,
      section: props?.section,
      formDefaults: {
        ...zodDefaultInstance(ClinicSchema),
        ...props?.defaults,
      },
      onPersist: async value => {
        await sync.mutate(value)
      },
    })

    const { form } = drawer

    return (
      <EntityDrawerShell drawer={drawer} showWarnings={props?.showGlobalImpactWarning}>
        <SectionFlow.Sections
          items={[
            {
              name: 'basic',
              title: t`Allgemein`,
              children: (
                <ProCard>
                  <Flex vertical gap="small">
                    <form.AppField
                      name="speciality"
                      children={field => (
                        <field.Select
                          className="col-span-3"
                          label="Spezialisierung"
                          options={MedicalSpecialityEnum.$.options}
                          render={v => MedicalSpecialityEnum.$.getLabel(v.value as typeof MedicalSpecialityEnum.$.Type)}
                          disabled={props?.showGlobalImpactWarning}
                        />
                      )}
                    />

                    <form.AppField
                      name="name"
                      children={field => (
                        <field.Text label="Name" className="col-span-6" disabled={props?.showGlobalImpactWarning} />
                      )}
                    />

                    <form.AppField
                      name="addition"
                      children={field => <field.Text label="Extra" className="col-span-3" />}
                    />

                    <AddressFieldGroup form={form} fields="address" />
                  </Flex>
                </ProCard>
              ),
            },
            {
              name: 'misc',
              title: t`Sonstiges`,
              children: (
                <ProCard>
                  <Flex vertical gap="small">
                    <form.AppField
                      name="comment"
                      children={field => <field.TextArea label={t`Bemerkungen`} className="grow" />}
                    />
                  </Flex>
                </ProCard>
              ),
            },
          ]}
        />
      </EntityDrawerShell>
    )
  },
)
